import React from "react";
import { addDoc, collection, GeoPoint } from "@firebase/firestore";
import { Controller, useForm } from "react-hook-form";
import { useFirestore } from "reactfire";

import { ContactType } from "@/domain/models/common";
import { useTranslator } from "@/i18n/useTranslator";
import { useCustomersListQuery } from "@/src/data/queries/customerQueries";

import { AddressField } from "../AddressField";
import {
  DsButton,
  DsComboBox,
  DsDivider,
  DsInput,
  DsSelectBox,
  DsText,
  DsTextArea,
  DsToggleGroup,
} from "../ui";

type CustomerType = {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  contacts: ContactType[];
};

type NewSiteFormType = {
  customer: string;
  customerId: string;
  contact: string;
  address: string;
  geopoint: GeoPoint;
  lockbox: string;
  floorSqft: string;
  floorsCleaned: string;
  notes: string;
};

type NewSiteFormPropsType = {
  onSuccess?: () => void;
  quickAdd?: boolean;
  detailsAdd?: boolean;
  passedCustomer?: CustomerType;
  passedContact?: ContactType | null;
};

const floorSqftOptions = [
  { label: "0-800", value: "0-800" },
  { label: "800-1500", value: "800-1500" },
  { label: "1500+", value: "1500+" },
];

export const NewSiteForm: React.FC<NewSiteFormPropsType> = ({
  onSuccess,
  quickAdd = false,
  detailsAdd = false,
  passedCustomer,
  passedContact = null,
}) => {
  const { t } = useTranslator();
  const firestore = useFirestore();
  const sitesRef = collection(firestore, "sites");
  const { data: customers, isLoading: isLoadingCustomers } =
    useCustomersListQuery();

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<NewSiteFormType>({
    defaultValues: {
      customer: passedCustomer?.id ?? "",
      customerId: passedCustomer?.id ?? "",
      contact: passedContact?.email ?? "",
      address: "",
      lockbox: "",
      floorSqft: "",
      floorsCleaned: "",
      notes: "",
    },
  });

  const selectedCustomer = watch("customer");
  const selectedCustomerData = customers?.find(
    customer => customer.id === selectedCustomer,
  );

  const customerOptions = React.useMemo(
    () =>
      customers?.map(customer => ({
        label: customer.name,
        value: customer.id,
      })) ?? [],
    [customers],
  );

  const contactOptions = React.useMemo(
    () =>
      selectedCustomerData?.contacts?.map(contact => ({
        label: contact.name,
        value: contact.email,
        id: contact.email,
      })) ?? [],
    [selectedCustomerData?.contacts],
  );

  const onSubmit = async (data: NewSiteFormType) => {
    if (!data.address) {
      return;
    }

    const siteData = quickAdd
      ? {
          ...data,
          customer: passedCustomer,
          customerId: passedCustomer?.id ?? "",
          contact: passedContact,
          floorsCleaned: "",
          lockbox: "",
          notes: "",
        }
      : detailsAdd
        ? {
            ...data,
            customer: passedCustomer,
            customerId: passedCustomer?.id ?? "",
            contact: passedCustomer?.contacts[0] ?? "",
          }
        : {
            ...data,
            customer: selectedCustomerData,
            customerId: selectedCustomerData?.id ?? "",
          };

    await addDoc(sitesRef, siteData);
    reset();
    onSuccess?.();
  };

  if (quickAdd) {
    return (
      <form
        onSubmit={e => {
          void handleSubmit(onSubmit)(e);
        }}
        className="mt-2 bg-gray-50 px-2 border-2 border-gray-200 rounded">
        <div className="mb-4">
          <AddressField
            watch={watch}
            setValue={setValue}
            register={register}
            errors={{
              address: errors.address
                ? {
                    type: errors.address.type,
                    message: errors.address.message ?? "",
                  }
                : undefined,
            }}
            label={t("add_new_job_drawer.site_address")}
            errorLabel={t("add_new_job_drawer.rules.required", {
              key: "address",
            })}
            fieldName="address"
          />
        </div>
        <DsButton type="submit" className="w-full">
          {t("add_new_job_drawer.add_site")}
        </DsButton>
      </form>
    );
  }

  return (
    <form
      onSubmit={e => {
        void handleSubmit(onSubmit)(e);
      }}
      className="mt-2 mb-24 space-y-4">
      {!detailsAdd && (
        <>
          <DsText variant="button">
            {t("add_new_job_drawer.select_a_customer")}
          </DsText>
          <Controller
            name="customer"
            control={control}
            rules={{
              required: t("add_new_job_drawer.rules.required", {
                key: "customer",
              }),
            }}
            render={({ field }) => (
              <DsComboBox
                selectedValue={field.value}
                onSelect={e => {
                  field.onChange(e);
                  if (e) {
                    setValue("customerId", e.value);
                    setValue("contact", "");
                  }
                }}
                isLoading={isLoadingCustomers}
                items={customerOptions}
              />
            )}
          />
        </>
      )}

      {(selectedCustomer || detailsAdd) && (
        <>
          <DsDivider />
          <DsText variant="button">
            {t("add_new_job_drawer.select_contact")}
          </DsText>
          <Controller
            name="contact"
            control={control}
            defaultValue={selectedCustomerData?.contacts?.[0]?.email}
            render={({ field }) => (
              <DsSelectBox
                selectedValue={field.value ?? ""}
                onSelect={e => field.onChange(e)}
                items={contactOptions}
              />
            )}
          />
          <DsDivider />

          <DsText variant="button">
            {t("add_new_job_drawer.site_address")}
          </DsText>
          <Controller
            name="address"
            control={control}
            rules={{
              required: t("add_new_job_drawer.rules.required", {
                key: "address",
              }),
            }}
            render={({ field }) => (
              <AddressField
                watch={watch}
                setValue={setValue}
                register={register}
                errors={{
                  address: errors.address
                    ? {
                        type: errors.address.type,
                        message: errors.address.message ?? "",
                      }
                    : undefined,
                }}
                errorLabel={t("add_new_job_drawer.rules.required", {
                  key: "address",
                })}
                fieldName="address"
              />
            )}
          />
          <DsDivider />

          <DsText variant="button">{t("site.lockbox")}</DsText>
          <Controller
            name="lockbox"
            control={control}
            render={({ field }) => <DsInput {...field} />}
          />
          <DsDivider />

          <DsText variant="button">{t("site.floor_sqft")}</DsText>
          <Controller
            name="floorSqft"
            control={control}
            render={({ field }) => {
              const handleFloorSqftChange = (value: string) => {
                field.onChange(value);
              };

              return (
                <DsToggleGroup
                  variant="flex"
                  value={field.value}
                  onChange={handleFloorSqftChange}
                  options={floorSqftOptions}
                />
              );
            }}
          />
          <DsDivider />

          <DsText variant="button">{t("site.total_floors")}</DsText>
          <Controller
            name="floorsCleaned"
            control={control}
            render={({ field }) => <DsInput {...field} type="number" />}
          />
          <DsDivider />

          <DsText variant="button">{t("notes")}</DsText>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => <DsTextArea {...field} />}
          />

          <DsButton type="submit" className="w-full">
            {t("add_new_job_drawer.add_site")}
          </DsButton>
        </>
      )}
    </form>
  );
};
